import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FilterToggle, FiltersPanel, SearchableMultiSelect } from '@components/filters';
import { stylePresetsByKey } from '@data/stylePresets';
import { COMPONENT_LABELS, getComponentLabel } from '@data/componentLabels';
import type { PublicPlan } from '@data/publicPlans';
import { useAuth } from '@services/auth';
import {
	getCompanyInvitePlans,
	getAdminDirectPlans,
	getAdminDirectPlansSuggest,
	createPlanInvite,
	inviteClientToPlan,
	type DirectPlansSuggestion
} from '@services/planInvites';
import { buildAppUrl } from '@utils/appBase';
import { getImageUrl } from '@utils/imageUrl';
import { mapApiPlanToPublicPlan } from '@utils/mapApiPlanToPublicPlan';
import { PLAN_CARD_FALLBACK_IMAGE, getPlanCardCoverCandidates } from '@utils/planCoverImage';
import { useI18n } from '../../i18n';
import { useAdminViewAs, getAdminEffectiveCompanyOrGardenerIdNum } from '../../lib/adminViewAs';
import { useAlertDialog } from '../../shared/AlertDialog';
import InviteSharePanel from '@components/plan/InviteSharePanel';
import ShareVisibilitySettings from '@components/plan/ShareVisibilitySettings';
import ClientVisibilityModal from '@components/plan/ClientVisibilityModal';
import { DEFAULT_SHARE_VISIBILITY, normalizeShareVisibility, summarizeShareVisibility, shareVisibilityIsRestricted, type ShareVisibility } from '@utils/shareVisibility';

/** One row per database plan id (API should not duplicate, but guard against bad data). */
function dedupeDirectPlansByDatabaseId(plans: PublicPlan[]): PublicPlan[] {
	const seen = new Set<number>();
	const result: PublicPlan[] = [];
	for (const plan of plans) {
		if (plan.databaseId != null) {
			if (seen.has(plan.databaseId)) continue;
			seen.add(plan.databaseId);
		}
		result.push(plan);
	}
	return result;
}

/** Cover image with fallbacks when the primary URL fails (e.g. draft with missing or broken “after” image). */
function DirectPlanCoverImage({ candidates, alt }: { candidates: string[]; alt: string }) {
	const candidatesKey = candidates.join('\u0001');
	const resolved = useMemo(() => {
		const out: string[] = [];
		const seen = new Set<string>();
		for (const raw of candidates) {
			const r = (raw || '').trim();
			if (!r) continue;
			const abs = getImageUrl(r) || r;
			if (seen.has(abs)) continue;
			seen.add(abs);
			out.push(abs);
		}
		return out;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by serialized list
	}, [candidatesKey]);

	const [index, setIndex] = useState(0);

	useEffect(() => {
		setIndex(0);
	}, [candidatesKey]);

	const src = resolved[index] ?? resolved[0] ?? PLAN_CARD_FALLBACK_IMAGE;

	return (
		<img
			src={src}
			className="card-img-top"
			alt={alt}
			style={{ height: '220px', objectFit: 'cover', width: '100%' }}
			onError={() => setIndex((i) => (i < resolved.length - 1 ? i + 1 : i))}
		/>
	);
}

export default function DirectPlans() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const { showAlert } = useAlertDialog();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';

	const getLocalizedStyleLabel = (styleKey?: string) => {
		switch (styleKey) {
			case 'modern':
				return copy('Modern', 'Modern', 'Moderni');
			case 'mediterranean':
				return copy('Mediterranean', 'Mediterran', 'Mediteranski');
			case 'minimalist':
				return copy('Minimalist', 'Minimalistisch', 'Minimalistički');
			case 'cottage':
				return copy('Cottage', 'Landhaus', 'Vikendica');
			case 'japanese':
				return copy('Japanese Zen', 'Japanischer Zen', 'Japanski Zen');
			case 'tropical':
				return copy('Tropical', 'Tropisch', 'Tropski');
			case 'rustic':
				return copy('Rustic', 'Rustikal', 'Rustikalni');
			case 'coastal':
				return copy('Coastal', 'Küstenstil', 'Obalni');
			case 'scandinavian':
				return copy('Scandinavian', 'Skandinavisch', 'Skandinavski');
			case 'traditional':
				return copy('Traditional', 'Traditionell', 'Tradicionalni');
			default:
				return stylePresetsByKey[styleKey as keyof typeof stylePresetsByKey]?.title ?? String(styleKey || '');
		}
	};

	const getLocalizedComponentLabel = (componentId: string) => {
		switch (componentId) {
			case 'pool':
				return copy('Pool', 'Pool', 'Bazen');
			case 'fence':
				return copy('Fence', 'Zaun', 'Ograda');
			case 'bbq':
				return copy('BBQ', 'Grill', 'Roštilj');
			case 'lounge':
				return copy('Lounge', 'Lounge', 'Zona za odmor');
			case 'outdoor-kitchen':
				return copy('Outdoor Kitchen', 'Außenküche', 'Vanjska kuhinja');
			case 'fire-pit':
				return copy('Fire Pit', 'Feuerstelle', 'Ložište');
			case 'pergola':
				return copy('Pergola', 'Pergola', 'Pergola');
			case 'deck':
				return copy('Deck', 'Terrasse', 'Terasa');
			case 'shed':
				return copy('Shed', 'Schuppen', 'Vrtna kućica');
			case 'playground':
				return copy('Playground', 'Spielplatz', 'Igralište');
			case 'vegetable-garden':
				return copy('Vegetable Garden', 'Gemüsegarten', 'Povrtnjak');
			case 'water-feature':
				return copy('Water Feature', 'Wasserelement', 'Vodeni element');
			case 'lighting':
				return copy('Lighting', 'Beleuchtung', 'Rasvjeta');
			case 'pathway':
				return copy('Pathway', 'Weg', 'Staza');
			case 'retaining-wall':
				return copy('Retaining Wall', 'Stützmauer', 'Potporni zid');
			case 'driveway':
				return copy('Driveway', 'Einfahrt', 'Prilaz');
			case 'greenhouse':
				return copy('Greenhouse', 'Gewächshaus', 'Staklenik');
			case 'flower-beds':
				return copy('Flower Beds', 'Blumenbeete', 'Cvjetne gredice');
			case 'hot-tub':
				return copy('Hot Tub', 'Whirlpool', 'Masažni bazen');
			case 'pizza-oven':
				return copy('Pizza Oven', 'Pizzaofen', 'Peć za pizzu');
			case 'compost':
				return copy('Compost Area', 'Kompostbereich', 'Kompost zona');
			default:
				return getComponentLabel(componentId);
		}
	};

	const shouldUseCompanyInviteApi = Boolean(
		user?.role === 'company' || (user?.role === 'admin' && viewAsTarget?.role === 'company')
	);
	const shouldUseAdminDirectApi = Boolean(user?.role === 'admin' && !shouldUseCompanyInviteApi);

	const companyActingUserId = useMemo(() => {
		if (!user) return null;
		if (user.role === 'company') {
			const n = parseInt(String(user.id), 10);
			return Number.isFinite(n) ? n : null;
		}
		if (user.role === 'admin' && viewAsTarget?.role === 'company') {
			return viewAsTarget.id;
		}
		return null;
	}, [user, viewAsTarget]);

	const [plans, setPlans] = useState<PublicPlan[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [directFilter, setDirectFilter] = useState<'all' | 'unassigned' | 'assigned' | 'drafts'>('all');
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStyles, setSelectedStyles] = useState<Array<PublicPlan['styleKey']>>([]);
	const [selectedCities, setSelectedCities] = useState<string[]>([]);
	const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'city' | 'style' | 'distance'>('newest');
	const [locationFilter, setLocationFilter] = useState<string>('');
	const [userCoordinates, setUserCoordinates] = useState<{ lat: number; lon: number } | null>(null);
	const [cityCoordinates, setCityCoordinates] = useState<Map<string, { lat: number; lon: number }>>(new Map());
	const [locationCoordinates, setLocationCoordinates] = useState<{ lat: number; lon: number } | null>(null);
	const [isGeocodingLocation, setIsGeocodingLocation] = useState(false);
	const [isGettingLocation, setIsGettingLocation] = useState(false);
	const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
	const [isGeocodingCities, setIsGeocodingCities] = useState(false);
	const [directUserSearchInput, setDirectUserSearchInput] = useState('');
	const [directUserSearch, setDirectUserSearch] = useState('');
	const [directSuggestions, setDirectSuggestions] = useState<DirectPlansSuggestion[]>([]);
	const [directSuggestionsLoading, setDirectSuggestionsLoading] = useState(false);
	const [showDirectSuggestions, setShowDirectSuggestions] = useState(false);
	const directFilterRef = useRef<HTMLDivElement>(null);

	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
	const [inviteExpiresAt, setInviteExpiresAt] = useState('');
	const [isCreatingInvite, setIsCreatingInvite] = useState(false);
	const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);
	const [createdShareText, setCreatedShareText] = useState<string | null>(null);
	const [inviteError, setInviteError] = useState<string | null>(null);
	const [invitePlanId, setInvitePlanId] = useState<number | null>(null);
	const [inviteEmail, setInviteEmail] = useState('');
	const [inviteAllowEdit, setInviteAllowEdit] = useState(true);
	const [referralShareVisibility, setReferralShareVisibility] = useState<ShareVisibility>(DEFAULT_SHARE_VISIBILITY);
	const [perPlanShareVisibility, setPerPlanShareVisibility] = useState<ShareVisibility>(DEFAULT_SHARE_VISIBILITY);
	const [inviteBusy, setInviteBusy] = useState(false);
	const [inviteCode, setInviteCode] = useState<string | null>(null);
	const [inviteShareText, setInviteShareText] = useState<string | null>(null);
	const [visibilityPlan, setVisibilityPlan] = useState<PublicPlan | null>(null);

	const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
		const R = 6371;
		const dLat = ((lat2 - lat1) * Math.PI) / 180;
		const dLon = ((lon2 - lon1) * Math.PI) / 180;
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	};

	const loadPlans = useCallback(async () => {
		if (!user) {
			setPlans([]);
			setIsLoading(false);
			return;
		}
		setIsLoading(true);
		setLoadError(null);
		try {
			if (shouldUseAdminDirectApi) {
				const apiPlans = await getAdminDirectPlans(user.id, directUserSearch.trim() || undefined);
				const converted = dedupeDirectPlansByDatabaseId(
					apiPlans
						.filter((p) => p.moderation_status !== 'hidden' && p.moderation_status !== 'deleted')
						.map((p) => mapApiPlanToPublicPlan(p))
				);
				setPlans(converted);
			} else if (shouldUseCompanyInviteApi && companyActingUserId) {
				const apiPlans = await getCompanyInvitePlans(companyActingUserId);
				const converted = dedupeDirectPlansByDatabaseId(
					apiPlans
						.filter((p) => p.moderation_status !== 'hidden' && p.moderation_status !== 'deleted')
						.map((p) => mapApiPlanToPublicPlan(p))
				);
				setPlans(converted);
			} else {
				setPlans([]);
			}
		} catch (e) {
			console.error('[DirectPlans] load failed', e);
			setLoadError(e instanceof Error ? e.message : 'Could not load plans.');
			setPlans([]);
		} finally {
			setIsLoading(false);
		}
	}, [user, shouldUseAdminDirectApi, shouldUseCompanyInviteApi, companyActingUserId, directUserSearch]);

	useEffect(() => {
		void loadPlans();
	}, [loadPlans]);

	useEffect(() => {
		if (!shouldUseAdminDirectApi) return;
		const t = setTimeout(() => setDirectUserSearch(directUserSearchInput.trim()), 400);
		return () => clearTimeout(t);
	}, [directUserSearchInput, shouldUseAdminDirectApi]);

	useEffect(() => {
		if (!shouldUseAdminDirectApi || directUserSearchInput.trim().length < 2) {
			setDirectSuggestions([]);
			setShowDirectSuggestions(false);
			return;
		}
		const q = directUserSearchInput.trim();
		const t = setTimeout(() => {
			setDirectSuggestionsLoading(true);
			getAdminDirectPlansSuggest(user!.id, q)
				.then((res) => {
					setDirectSuggestions(res.suggestions ?? []);
					setShowDirectSuggestions((res.suggestions?.length ?? 0) > 0);
				})
				.catch(() => {
					setDirectSuggestions([]);
					setShowDirectSuggestions(false);
				})
				.finally(() => setDirectSuggestionsLoading(false));
		}, 300);
		return () => clearTimeout(t);
	}, [directUserSearchInput, user, shouldUseAdminDirectApi]);

	useEffect(() => {
		if (!showDirectSuggestions) return;
		const onDocClick = (e: MouseEvent) => {
			if (directFilterRef.current && !directFilterRef.current.contains(e.target as Node)) {
				setShowDirectSuggestions(false);
			}
		};
		document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [showDirectSuggestions]);

	const activePlans = useMemo(
		() => plans.filter((p) => (p.status === 'active' || p.status === 'draft') && p.moderationStatus !== 'hidden'),
		[plans]
	);

	const geocodeLocation = async (location: string): Promise<{ lat: number; lon: number } | null> => {
		if (!location.trim()) return null;
		try {
			const response = await fetch(
				`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&limit=1&addressdetails=1`,
				{ headers: { 'User-Agent': 'HortEra Direct Plans' } }
			);
			const data = await response.json();
			if (data && data.length > 0) {
				return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
			}
		} catch {
			/* ignore */
		}
		return null;
	};

	const handleUseMyLocation = () => {
		if (!navigator.geolocation) return;
		setIsGettingLocation(true);
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				try {
					const { latitude, longitude } = position.coords;
					setUserCoordinates({ lat: latitude, lon: longitude });
					const response = await fetch(
						`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
						{ headers: { 'User-Agent': 'HortEra Direct Plans' } }
					);
					const data = await response.json();
					const city =
						data.address?.city ||
						data.address?.town ||
						data.address?.village ||
						data.address?.municipality ||
						data.address?.county ||
						data.address?.state ||
						'';
					if (city) {
						setLocationFilter(city);
						setLocationCoordinates({ lat: latitude, lon: longitude });
					}
				} finally {
					setIsGettingLocation(false);
				}
			},
			() => setIsGettingLocation(false),
			{ enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
		);
	};

	useEffect(() => {
		if ('permissions' in navigator && navigator.permissions) {
			(navigator.permissions as { query: (o: { name: string }) => Promise<PermissionStatus> })
				.query({ name: 'geolocation' })
				.then((result) => {
					setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
					if (result.state === 'granted' && !locationFilter && !userCoordinates) {
						handleUseMyLocation();
					}
				})
				.catch(() => {
					setPermissionState('unknown');
					if (navigator.geolocation && !locationFilter && !userCoordinates) {
						handleUseMyLocation();
					}
				});
		} else {
			setPermissionState('unknown');
			if (navigator.geolocation && !locationFilter && !userCoordinates) {
				handleUseMyLocation();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const timeoutId = setTimeout(async () => {
			if (locationFilter.trim() && sortBy === 'distance') {
				if (userCoordinates && locationCoordinates) return;
				setIsGeocodingLocation(true);
				const coords = await geocodeLocation(locationFilter.trim());
				setLocationCoordinates(coords);
				setIsGeocodingLocation(false);
			} else if (!locationFilter.trim()) {
				setLocationCoordinates(null);
			}
		}, 800);
		return () => clearTimeout(timeoutId);
	}, [locationFilter, sortBy, userCoordinates, locationCoordinates]);

	useEffect(() => {
		if (sortBy === 'distance' && (userCoordinates || locationCoordinates)) {
			const uniqueCities = Array.from(new Set(activePlans.map((p) => p.city)));
			const citiesToGeocode = uniqueCities.filter((city) => !cityCoordinates.has(city));
			if (citiesToGeocode.length > 0) {
				setIsGeocodingCities(true);
				Promise.all(citiesToGeocode.map((city) => geocodeLocation(city).then((coords) => ({ city, coords }))))
					.then((results) => {
						setCityCoordinates((prev) => {
							const newMap = new Map(prev);
							for (const { city, coords } of results) {
								if (coords) newMap.set(city, coords);
							}
							return newMap;
						});
					})
					.finally(() => setIsGeocodingCities(false));
			} else {
				setIsGeocodingCities(false);
			}
		} else {
			setIsGeocodingCities(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortBy, userCoordinates, locationCoordinates, activePlans]);

	const filteredPlans = useMemo(() => {
		const normalizedSearch = searchTerm.trim().toLowerCase();
		let filtered = activePlans.filter((plan) => {
			const matchesDirect =
				directFilter === 'all'
					? true
					: directFilter === 'unassigned'
						? !plan.hasLinkedSeeker
						: directFilter === 'assigned'
							? Boolean(plan.hasLinkedSeeker)
							: plan.status === 'draft';
			const matchesStyle = selectedStyles.length === 0 || selectedStyles.includes(plan.styleKey);
			const matchesCity = selectedCities.length === 0 || selectedCities.includes(plan.city);
			const planComponents = plan.questionnaire?.selectedComponents ?? [];
			const matchesComponents =
				selectedComponents.length === 0 || selectedComponents.some((c) => planComponents.includes(c));
			if (!normalizedSearch) {
				return matchesDirect && matchesStyle && matchesCity && matchesComponents;
			}
			const styleTitle = stylePresetsByKey[plan.styleKey]?.title ?? '';
			const matchesSearch =
				plan.title.toLowerCase().includes(normalizedSearch) ||
				plan.city.toLowerCase().includes(normalizedSearch) ||
				styleTitle.toLowerCase().includes(normalizedSearch);
			return matchesDirect && matchesStyle && matchesCity && matchesComponents && matchesSearch;
		});

		const sorted = [...filtered].sort((a, b) => {
			switch (sortBy) {
				case 'newest':
					return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
				case 'oldest':
					return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
				case 'city':
					return a.city.localeCompare(b.city);
				case 'style': {
					const styleA = stylePresetsByKey[a.styleKey]?.title ?? '';
					const styleB = stylePresetsByKey[b.styleKey]?.title ?? '';
					return styleA.localeCompare(styleB);
				}
				case 'distance': {
					const referenceCoords = locationCoordinates || userCoordinates;
					if (referenceCoords) {
						const aCoords = cityCoordinates.get(a.city);
						const bCoords = cityCoordinates.get(b.city);
						if (aCoords && bCoords) {
							return (
								calculateDistance(referenceCoords.lat, referenceCoords.lon, aCoords.lat, aCoords.lon) -
								calculateDistance(referenceCoords.lat, referenceCoords.lon, bCoords.lat, bCoords.lon)
							);
						}
						if (aCoords && !bCoords) return -1;
						if (!aCoords && bCoords) return 1;
					}
					if (locationFilter.trim()) {
						const locationLower = locationFilter.trim().toLowerCase();
						const aMatches =
							a.city.toLowerCase().includes(locationLower) || locationLower.includes(a.city.toLowerCase());
						const bMatches =
							b.city.toLowerCase().includes(locationLower) || locationLower.includes(b.city.toLowerCase());
						if (aMatches && !bMatches) return -1;
						if (!aMatches && bMatches) return 1;
					}
					return a.city.localeCompare(b.city);
				}
				default:
					return 0;
			}
		});
		return sorted;
	}, [
		activePlans,
		searchTerm,
		selectedStyles,
		selectedCities,
		selectedComponents,
		sortBy,
		locationFilter,
		userCoordinates,
		locationCoordinates,
		cityCoordinates,
		directFilter
	]);

	const styleOptions = useMemo(() => Object.entries(stylePresetsByKey), []);
	const cityOptions = useMemo(
		() => Array.from(new Set(activePlans.map((plan) => plan.city))).sort(),
		[activePlans]
	);
	const componentOptions = useMemo(
		() => Object.entries(COMPONENT_LABELS).map(([value]) => ({ value, label: getLocalizedComponentLabel(value) })),
		[language]
	);

	const activeFiltersCount = useMemo(() => {
		let count = 0;
		count += selectedStyles.length;
		count += selectedCities.length;
		count += selectedComponents.length;
		return count;
	}, [selectedStyles, selectedCities, selectedComponents]);

	const toggleSelection = <T,>(value: T, collection: T[], setCollection: (items: T[]) => void) => {
		setCollection(collection.includes(value) ? collection.filter((item) => item !== value) : [...collection, value]);
	};

	const handleResetFilters = () => {
		setSelectedStyles([]);
		setSelectedCities([]);
		setSelectedComponents([]);
	};

	const uniqueCities = useMemo(() => new Set(filteredPlans.map((p) => p.city)).size, [filteredPlans]);

	const isNewPlan = (plan: PublicPlan) => {
		const daysSinceCreation = (Date.now() - new Date(plan.createdAt).getTime()) / (1000 * 60 * 60 * 24);
		return daysSinceCreation <= 7;
	};

	/** Exactly one of: client linked → invite sent (pending) → no client linked. */
	const getClientLinkBadge = (plan: PublicPlan): { label: string; badgeClass: string } => {
		const linked = Boolean(plan.hasLinkedSeeker);
		if (linked) {
			return {
				label: copy('Client linked', 'Kunde verknüpft', 'Klijent povezan'),
				badgeClass: 'text-bg-info'
			};
		}
		const inviteSent = Boolean(plan.hasClientInvite);
		if (inviteSent) {
			return {
				label: copy('Invite sent', 'Einladung gesendet', 'Pozivnica poslana'),
				badgeClass: 'text-bg-secondary'
			};
		}
		return {
			label: copy('No client linked', 'Kein Kunde verknüpft', 'Nema povezanog klijenta'),
			badgeClass: 'text-bg-warning'
		};
	};

	const openPerPlanInvite = (planDbId: number | undefined) => {
		if (!planDbId) return;
		const existing = plans.find((p) => p.databaseId === planDbId);
		setInvitePlanId(planDbId);
		setInviteEmail('');
		setInviteAllowEdit(true);
		setPerPlanShareVisibility(normalizeShareVisibility(existing?.share_visibility));
		setInviteCode(null);
		setInviteShareText(null);
	};

	const closePerPlanInvite = () => {
		if (inviteBusy) return;
		setInvitePlanId(null);
	};

	const perPlanInviteUserId = useMemo(() => {
		const n = getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget);
		if (n != null) return n;
		if (user?.role === 'company') {
			const x = parseInt(String(user.id), 10);
			return Number.isFinite(x) ? x : null;
		}
		return null;
	}, [user, viewAsTarget]);

	const submitPerPlanInvite = async () => {
		if (!invitePlanId || !perPlanInviteUserId) return;
		setInviteBusy(true);
		try {
			const res = await inviteClientToPlan({
				planId: invitePlanId,
				userId: perPlanInviteUserId,
				invitedEmail: inviteEmail.trim() || undefined,
				allowEdit: inviteAllowEdit,
				shareVisibility: perPlanShareVisibility
			});
			const code = res.code ?? null;
			setInviteCode(code);
			if (code) {
				const inviteUrl = buildAppUrl(`?invite=${encodeURIComponent(code)}`);
				setInviteShareText(
					copy(
						`Hey! I shared a garden plan with you on HortEra:\n${inviteUrl}\n\nLog in or sign up through this link to view it.`,
						`Hallo! Ich habe einen Gartenplan mit dir auf HortEra geteilt:\n${inviteUrl}\n\nMelde dich über diesen Link an oder registriere dich, um ihn zu sehen.`,
						`Bok! Podijelio/la sam vrtni plan s tobom na HortEri:\n${inviteUrl}\n\nPrijavi se ili registriraj putem ove poveznice da ga vidiš.`
					)
				);
			}
			showAlert(
				copy('Invite created. Share the link with your client.', 'Einladung erstellt. Teilen Sie den Link mit Ihrem Kunden.', 'Pozivnica je kreirana. Podijelite poveznicu s klijentom.'),
				{ title: copy('Invite ready', 'Einladung bereit', 'Pozivnica spremna') }
			);
			await loadPlans();
		} catch (err) {
			console.error(err);
			showAlert(err instanceof Error ? err.message : copy('Invite failed.', 'Einladung fehlgeschlagen.', 'Pozivnica nije uspjela.'), {
				title: copy('Error', 'Fehler', 'Greška')
			});
		} finally {
			setInviteBusy(false);
		}
	};

	if (user?.role === 'admin' && shouldUseCompanyInviteApi && !companyActingUserId) {
		return (
			<div className="container py-5">
				<div className="alert alert-info">
					{copy(
						'Use “View as…” and pick a company account to see that company’s direct plans.',
						'Nutzen Sie „Ansehen als…“ und wählen Sie ein Unternehmenskonto, um dessen direkte Pläne zu sehen.',
						'Koristite „Pogledaj kao…“ i odaberite račun tvrtke da vidite izravne planove te tvrtke.'
					)}
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="marketplace-hero mb-4 rounded-4 p-4 p-lg-5 position-relative overflow-hidden">
				<div className="position-absolute top-0 start-0 w-100 h-100 marketplace-hero-bg"></div>
				<div className="position-relative">
					<div className="row align-items-center g-4">
						<div className="col-12 col-lg-8">
							<div className="d-flex align-items-center gap-3 mb-3">
								<div className="marketplace-icon">
									<i className="bi bi-person-lines-fill"></i>
								</div>
								<div>
									<h1 className="h2 mb-1 fw-bold text-success-emphasis">
										{copy('Direct plans', 'Direkte Pläne', 'Izravni planovi')}
									</h1>
									<p className="text-muted mb-0">
										{copy(
											'Plans tied to your company — from your team or from clients — in one place.',
											'Pläne, die mit Ihrem Unternehmen verknüpft sind — von Ihrem Team oder von Kunden — an einem Ort.',
											'Planovi vezani uz vašu tvrtku — od vašeg tima ili klijenata — na jednom mjestu.'
										)}
									</p>
									<p className="small mt-2 mb-0">
										<Link to="/app/plans" className="text-success text-decoration-none fw-semibold">
											<i className="bi bi-shop me-1" />
											{copy('Browse public marketplace', 'Öffentlichen Marktplatz durchsuchen', 'Pregledaj javnu tržnicu')}
										</Link>
									</p>
								</div>
							</div>
						</div>
						<div className="col-12 col-lg-4">
							<div className="marketplace-stat-card p-3 rounded-3">
								<div className="d-flex align-items-center gap-2 mb-1">
									<i className="bi bi-collection text-success"></i>
									<span className="text-muted small">{copy('Direct plans', 'Direkte Pläne', 'Izravni planovi')}</span>
								</div>
								<div className="h4 mb-0 fw-bold text-success-emphasis">{filteredPlans.length}</div>
							</div>
						</div>
					</div>
					{user?.role === 'company' && (
						<div className="d-flex flex-wrap align-items-center gap-2 mt-3">
							<Link to="/app/seeker/create-plan?mode=company-direct" className="btn btn-sm btn-success">
								<i className="bi bi-plus-circle me-1" />
								{copy('Create direct plan', 'Direkten Plan erstellen', 'Kreiraj izravni plan')}
							</Link>
							<button
								type="button"
								className="btn btn-sm btn-outline-light text-success-emphasis border-success"
								onClick={() => {
									setInviteError(null);
									setCreatedInviteCode(null);
									setCreatedShareText(null);
									setInviteExpiresAt('');
									setReferralShareVisibility(DEFAULT_SHARE_VISIBILITY);
									setIsInviteModalOpen(true);
								}}
							>
								<i className="bi bi-person-plus me-1" />
								{copy('Invite client', 'Kunden einladen', 'Pozovi klijenta')}
							</button>
						</div>
					)}
					<div className="d-flex mt-2">
						<div className="btn-group" role="tablist" aria-label="Direct plan filter">
							<button
								type="button"
								className={`btn btn-sm ${directFilter === 'all' ? 'btn-success' : 'btn-outline-success'}`}
								onClick={() => setDirectFilter('all')}
							>
								{copy('All', 'Alle', 'Sve')}
							</button>
							<button
								type="button"
								className={`btn btn-sm ${directFilter === 'unassigned' ? 'btn-success' : 'btn-outline-success'}`}
								onClick={() => setDirectFilter('unassigned')}
							>
								{copy('Unassigned', 'Nicht zugeordnet', 'Nedodijeljeno')}
							</button>
							<button
								type="button"
								className={`btn btn-sm ${directFilter === 'assigned' ? 'btn-success' : 'btn-outline-success'}`}
								onClick={() => setDirectFilter('assigned')}
							>
								{copy('Assigned', 'Zugewiesen', 'Dodijeljeno')}
							</button>
							<button
								type="button"
								className={`btn btn-sm ${directFilter === 'drafts' ? 'btn-success' : 'btn-outline-success'}`}
								onClick={() => setDirectFilter('drafts')}
							>
								{copy('Drafts', 'Entwürfe', 'Skice')}
							</button>
						</div>
					</div>
				</div>
			</div>

			<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
				{shouldUseAdminDirectApi && (
					<div ref={directFilterRef} className="d-flex align-items-center gap-2 flex-wrap position-relative">
						<label className="text-muted small mb-0">{copy('Filter:', 'Filter:', 'Filter:')}</label>
						<div className="position-relative">
							<input
								type="search"
								className="form-control form-control-sm"
								placeholder={copy('Seeker, email, or company…', 'Suchender, E-Mail oder Firma…', 'Tražitelj, e-mail ili tvrtka…')}
								value={directUserSearchInput}
								onChange={(e) => setDirectUserSearchInput(e.target.value)}
								onFocus={() => directSuggestions.length > 0 && setShowDirectSuggestions(true)}
								style={{ width: '260px' }}
								autoComplete="off"
							/>
							{directSuggestionsLoading && (
								<span className="position-absolute end-0 top-50 translate-middle-y me-2">
									<span className="spinner-border spinner-border-sm text-muted" role="status" aria-hidden="true" />
								</span>
							)}
							{showDirectSuggestions && directSuggestions.length > 0 && (
								<ul
									className="list-group position-absolute start-0 mt-1 shadow-sm border rounded"
									style={{ width: '320px', maxHeight: '280px', overflowY: 'auto', zIndex: 1050 }}
								>
									{directSuggestions.map((s) => (
										<li
											key={`${s.type}-${s.id}`}
											className="list-group-item list-group-item-action py-2 cursor-pointer"
											onClick={() => {
												setDirectUserSearchInput(s.searchValue);
												setShowDirectSuggestions(false);
											}}
										>
											<div className="fw-medium small">{s.label}</div>
											{s.sublabel && <div className="text-muted small">{s.sublabel}</div>}
										</li>
									))}
								</ul>
							)}
						</div>
						{directUserSearchInput && (
							<button
								type="button"
								className="btn btn-sm btn-outline-secondary"
								onClick={() => {
									setDirectUserSearchInput('');
									setShowDirectSuggestions(false);
								}}
							>
								{copy('Clear', 'Löschen', 'Očisti')}
							</button>
						)}
					</div>
				)}
				<div className="flex-grow-1" style={{ maxWidth: '500px' }}>
					<div className="input-group">
						<span className="input-group-text bg-white border-end-0">
							<i className="bi bi-search text-muted"></i>
						</span>
						<input
							type="search"
							className="form-control border-start-0"
							placeholder={copy('Search by title, city, or style...', 'Suche nach Titel, Stadt oder Stil...', 'Pretraži po naslovu, gradu ili stilu...')}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							style={{ boxShadow: 'none' }}
						/>
					</div>
				</div>
				<div className="d-flex gap-2 align-items-center flex-wrap">
					<div className="d-flex align-items-center gap-2">
						<label className="text-muted small mb-0">{copy('Location:', 'Standort:', 'Lokacija:')}</label>
						<div className="input-group" style={{ width: 'auto' }}>
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder={copy('City or address...', 'Stadt oder Adresse...', 'Grad ili adresa...')}
								value={locationFilter}
								onChange={(e) => {
									setLocationFilter(e.target.value);
									if (e.target.value.trim() !== locationFilter) setLocationCoordinates(null);
								}}
								style={{ width: '180px' }}
							/>
							{(isGeocodingLocation || isGettingLocation) && (
								<span className="input-group-text bg-white">
									<span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true"></span>
								</span>
							)}
							<button
								type="button"
								className="btn btn-outline-success btn-sm"
								onClick={() => handleUseMyLocation()}
								disabled={isGettingLocation}
								title={
									permissionState === 'denied'
										? copy('Location denied — try again or enter manually', 'Standort verweigert', 'Lokacija odbijena')
										: copy('Use my location', 'Meinen Standort nutzen', 'Koristi moju lokaciju')
								}
							>
								<i className="bi bi-geo-alt-fill"></i>
							</button>
						</div>
					</div>
					<div className="d-flex align-items-center gap-2">
						<label className="text-muted small mb-0">{copy('Sort:', 'Sortieren:', 'Sortiraj:')}</label>
						<select
							className="form-select form-select-sm"
							style={{ width: 'auto', minWidth: '120px' }}
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
						>
							<option value="newest">{copy('Newest First', 'Neueste zuerst', 'Najnovije prvo')}</option>
							<option value="oldest">{copy('Oldest First', 'Älteste zuerst', 'Najstarije prvo')}</option>
							<option value="distance">{copy('Distance', 'Entfernung', 'Udaljenost')}</option>
							<option value="city">{copy('By City', 'Nach Stadt', 'Po gradu')}</option>
							<option value="style">{copy('By Style', 'Nach Stil', 'Po stilu')}</option>
						</select>
					</div>
					<FilterToggle isOpen={isFiltersOpen} onToggle={() => setIsFiltersOpen((p) => !p)} activeCount={activeFiltersCount} />
				</div>
			</div>

			{activeFiltersCount > 0 && (
				<div className="d-flex flex-wrap align-items-center gap-2 mb-4">
					{selectedStyles.map((styleKey) => (
						<span
							key={styleKey}
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setSelectedStyles(selectedStyles.filter((s) => s !== styleKey))}
						>
							{getLocalizedStyleLabel(styleKey)}
							<i className="bi bi-x"></i>
						</span>
					))}
					{selectedCities.map((city) => (
						<span
							key={city}
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setSelectedCities(selectedCities.filter((c) => c !== city))}
						>
							<i className="bi bi-geo-alt-fill"></i>
							{city}
							<i className="bi bi-x"></i>
						</span>
					))}
					{selectedComponents.map((componentId) => (
						<span
							key={componentId}
							className="badge text-bg-light text-dark d-flex align-items-center gap-1"
							style={{ cursor: 'pointer', fontSize: '0.875rem' }}
							onClick={() => setSelectedComponents(selectedComponents.filter((c) => c !== componentId))}
						>
							<i className="bi bi-grid-1x2-fill"></i>
							{getLocalizedComponentLabel(componentId)}
							<i className="bi bi-x"></i>
						</span>
					))}
					<button type="button" className="btn btn-sm btn-link text-muted p-0" onClick={handleResetFilters} style={{ fontSize: '0.875rem' }}>
						{copy('Clear all', 'Alles löschen', 'Očisti sve')}
					</button>
				</div>
			)}

			{user?.role === 'company' && isInviteModalOpen && (
				<>
					<div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
						<div className="modal-dialog modal-dialog-centered">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">
										{copy('Invite a client to create a plan', 'Kundin/Kunden zum Erstellen eines Plans einladen', 'Pozovi klijenta da izradi plan')}
									</h5>
									<button
										type="button"
										className="btn-close"
										aria-label="Close"
										onClick={() => {
											if (isCreatingInvite) return;
											setIsInviteModalOpen(false);
										}}
									/>
								</div>
								<div className="modal-body">
									<p className="text-muted small">
										{copy(
											'Create a private invite link. Plans from this link go to your company, not the public marketplace.',
											'Privater Einladungslink. Pläne aus dieser Einladung gehen an Ihr Unternehmen, nicht an den öffentlichen Marktplatz.',
											'Privatni pozivni link. Planovi s tog linka idu vašoj tvrtki, ne na javnu tržnicu.'
										)}
									</p>
									<div className="mb-3">
										<label className="form-label">{copy('Expires at (optional)', 'Gültig bis (optional)', 'Istječe (opcionalno)')}</label>
										<input
											type="datetime-local"
											className="form-control"
											value={inviteExpiresAt}
											onChange={(e) => setInviteExpiresAt(e.target.value)}
											disabled={isCreatingInvite || !!createdInviteCode}
										/>
									</div>
									<ShareVisibilitySettings
										value={referralShareVisibility}
										onChange={setReferralShareVisibility}
										copy={copy}
										disabled={isCreatingInvite || !!createdInviteCode}
										compact
									/>
									{inviteError && (
										<div className="alert alert-danger py-2 small" role="alert">
											{inviteError}
										</div>
									)}
									{createdInviteCode && createdShareText && (
										<InviteSharePanel
											inviteCode={createdInviteCode}
											shareText={createdShareText}
											copy={copy}
											title={copy('Invite created', 'Einladung erstellt', 'Pozivnica kreirana')}
										/>
									)}
								</div>
								<div className="modal-footer">
									<button
										type="button"
										className={`btn ${createdInviteCode ? 'btn-success' : 'btn-outline-secondary'}`}
										onClick={() => {
											if (isCreatingInvite) return;
											setIsInviteModalOpen(false);
										}}
									>
										{copy('Close', 'Schließen', 'Zatvori')}
									</button>
									{!createdInviteCode && (
									<button
										type="button"
										className="btn btn-success"
										disabled={isCreatingInvite}
										onClick={async () => {
											if (!user?.id) return;
											const userIdNum = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
											if (!userIdNum || Number.isNaN(userIdNum)) {
												setInviteError('Missing user id');
												return;
											}
											setInviteError(null);
											setIsCreatingInvite(true);
											try {
												const invite = await createPlanInvite({
													userId: userIdNum,
													expiresAt: inviteExpiresAt || undefined,
													shareVisibility: referralShareVisibility
												});
												const code = invite.code;
												setCreatedInviteCode(code);
												const inviteUrl = buildAppUrl(`?invite=${encodeURIComponent(code)}`);
												setCreatedShareText(
													`Hey there! 🌿\n\nYou can create your dream garden plan on HortEra here:\n${inviteUrl}\n\nWhen you log in or sign up from this link, you’ll see our invitation for a free garden plan that comes directly to us.`
												);
											} catch (err: unknown) {
												const msg =
													err instanceof Error
														? err.message
														: copy('Failed to create invite.', 'Einladung konnte nicht erstellt werden.', 'Kreiranje pozivnice nije uspjelo.');
												setInviteError(msg);
											} finally {
												setIsCreatingInvite(false);
											}
										}}
									>
										{isCreatingInvite ? (
											<>
												<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
												{copy('Creating…', 'Wird erstellt…', 'Stvaranje…')}
											</>
										) : (
											<>
												<i className="bi bi-link-45deg me-1" />
												{copy('Create invite', 'Einladung erstellen', 'Stvori pozivnicu')}
											</>
										)}
									</button>
									)}
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			<FiltersPanel isOpen={isFiltersOpen} onApply={() => setIsFiltersOpen(false)} onReset={handleResetFilters}>
				<div className="row g-4">
					<div className="col-12 col-lg-4">
						<SearchableMultiSelect
							id="direct-style-filter"
							label={copy('Style', 'Stil', 'Stil')}
							placeholder={copy('Select style(s)...', 'Stil(e) auswählen...', 'Odaberite stil(ove)...')}
							options={styleOptions.map(([key]) => ({ value: key, label: getLocalizedStyleLabel(key) }))}
							selectedValues={selectedStyles}
							onToggle={(value) => toggleSelection(value as PublicPlan['styleKey'], selectedStyles, setSelectedStyles)}
							emptyText={copy('No style found.', 'Kein Stil gefunden.', 'Nije pronađen stil.')}
						/>
					</div>
					<div className="col-12 col-lg-4">
						<SearchableMultiSelect
							id="direct-city-filter"
							label={copy('City', 'Stadt', 'Grad')}
							placeholder={copy('Select city/cities...', 'Stadt/Städte auswählen...', 'Odaberite grad(ove)...')}
							options={cityOptions.map((city) => ({ value: city, label: city }))}
							selectedValues={selectedCities}
							onToggle={(value) => toggleSelection(value, selectedCities, setSelectedCities)}
							emptyText={copy('No cities yet.', 'Noch keine Städte.', 'Još nema gradova.')}
						/>
					</div>
					<div className="col-12 col-lg-4">
						<SearchableMultiSelect
							id="direct-component-filter"
							label={copy('Components', 'Komponenten', 'Komponente')}
							placeholder={copy('Select component(s)...', 'Komponente(n) auswählen...', 'Odaberite komponentu(e)...')}
							options={componentOptions}
							selectedValues={selectedComponents}
							onToggle={(value) => toggleSelection(value, selectedComponents, setSelectedComponents)}
							emptyText={copy('No components found.', 'Keine Komponenten gefunden.', 'Nema komponenti.')}
						/>
					</div>
				</div>
			</FiltersPanel>

			{loadError && <div className="alert alert-danger">{loadError}</div>}

			{isLoading ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<div className="spinner-border text-success mb-3" role="status">
							<span className="visually-hidden">{copy('Loading...', 'Laden...', 'Učitavanje...')}</span>
						</div>
						<p className="text-muted mb-0">{copy('Loading direct plans…', 'Direkte Pläne werden geladen…', 'Učitavanje izravnih planova…')}</p>
					</div>
				</div>
			) : filteredPlans.length === 0 ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<i className="bi bi-inbox display-4 text-muted mb-3 d-block"></i>
						<p className="text-muted mb-0">
							{activePlans.length === 0
								? copy(
										'No direct plans yet. Create one or invite a client.',
										'Noch keine direkten Pläne. Erstellen Sie einen oder laden Sie einen Kunden ein.',
										'Još nema izravnih planova. Izradite jedan ili pozovite klijenta.'
									)
								: copy(
										'No plans match your filters. Try adjusting your search.',
										'Keine Pläne entsprechen den Filtern. Passen Sie die Suche an.',
										'Nijedan plan ne odgovara filtrima. Pokušajte prilagoditi pretragu.'
									)}
						</p>
					</div>
				</div>
			) : (
				<>
					<div className="d-flex align-items-center justify-content-between mb-3">
						<div>
							<h3 className="h5 mb-1 fw-semibold text-success-emphasis">
								{filteredPlans.length}{' '}
								{filteredPlans.length === 1
									? copy('Direct plan', 'Direkter Plan', 'Izravni plan')
									: copy('Direct plans', 'Direkte Pläne', 'Izravni planovi')}{' '}
								{copy('available', 'verfügbar', 'dostupno')}
							</h3>
							<p className="text-muted small mb-0">
								{uniqueCities > 0 &&
									`${uniqueCities} ${uniqueCities === 1 ? copy('city', 'Stadt', 'grad') : copy('cities', 'Städte', 'gradova')} • `}
								{copy(
									'Review plans linked to your company and invite seekers when needed.',
									'Prüfen Sie pläne mit Firmenbezug und laden Sie bei Bedarf Suchende ein.',
									'Pregledajte planove vezane uz tvrtku i po potrebi pozovite tražitelje.'
								)}
							</p>
						</div>
					</div>
					<div className="position-relative">
						<div className="row g-4">
							{filteredPlans.map((p) => {
								const coverCandidates = getPlanCardCoverCandidates(p);
								const planHref = `/app/seeker/plan/${p.id}`;
								const clientBadge = getClientLinkBadge(p);
								return (
									<div key={p.id} className="col-12 col-md-6 col-lg-4">
										<div
											className="marketplace-card card h-100 border-0 shadow-sm position-relative overflow-hidden"
											role="link"
											tabIndex={0}
											onClick={() => navigate(planHref)}
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													navigate(planHref);
												}
											}}
											style={{ cursor: 'pointer' }}
										>
											<div className="marketplace-card-image position-relative">
												<DirectPlanCoverImage candidates={coverCandidates} alt={p.title} />
												<div className="position-absolute top-0 end-0 m-2 d-flex flex-column gap-1" style={{ alignItems: 'flex-end' }}>
													{isNewPlan(p) && (
														<span className="badge text-bg-warning shadow-sm">
															<i className="bi bi-star-fill me-1"></i>
															{copy('New', 'Neu', 'Novo')}
														</span>
													)}
													{p.status === 'draft' && (
														<span className="badge text-bg-secondary shadow-sm">{copy('Draft', 'Entwurf', 'Skica')}</span>
													)}
													<span className={`badge shadow-sm ${clientBadge.badgeClass}`}>{clientBadge.label}</span>
													{(p.hasClientInvite || p.hasLinkedSeeker) && shareVisibilityIsRestricted(p.share_visibility) && (
														<span className="badge text-bg-light text-muted border shadow-sm" title={summarizeShareVisibility(normalizeShareVisibility(p.share_visibility), copy)}>
															<i className="bi bi-eye-slash me-1" aria-hidden />
															{summarizeShareVisibility(normalizeShareVisibility(p.share_visibility), copy)}
														</span>
													)}
												</div>
												{p.aiGenerated && (
													<div className="position-absolute top-0 start-0 m-2">
														<span className="badge text-bg-primary shadow-sm">
															<i className="bi bi-magic me-1"></i>
															{copy('Design available', 'Design verfügbar', 'Dizajn dostupan')}
														</span>
													</div>
												)}
											</div>
											<div className="card-body d-flex flex-column">
												<div className="mb-3">
													<h4 className="h5 fw-bold text-success-emphasis mb-2">{p.title}</h4>
														<div className="d-flex align-items-center gap-2 text-muted small mb-2">
															<i className="bi bi-geo-alt-fill"></i>
															<span>{p.city}</span>
															<span>•</span>
															<i className="bi bi-palette-fill"></i>
															<span>{getLocalizedStyleLabel(p.styleKey)}</span>
														</div>
														{p.questionnaire?.selectedComponents && p.questionnaire.selectedComponents.length > 0 && (
															<div className="d-flex flex-wrap gap-2 mb-2">
																{p.questionnaire.selectedComponents.slice(0, 4).map((componentId) => (
																	<span
																		key={componentId}
																		className="badge bg-success-subtle text-success-emphasis border border-success-subtle"
																	>
																		<i className="bi bi-grid-1x2-fill me-1"></i>
																		{getLocalizedComponentLabel(componentId)}
																	</span>
																))}
																{p.questionnaire.selectedComponents.length > 4 && (
																	<span className="badge bg-light text-muted border">+{p.questionnaire.selectedComponents.length - 4}</span>
																)}
															</div>
														)}
														<div className="d-flex align-items-center gap-2 text-muted small mb-2">
															<i className="bi bi-calendar3"></i>
															<span>
																{copy('Posted', 'Veröffentlicht', 'Objavljeno')} {new Date(p.createdAt).toLocaleDateString(locale)}
															</span>
														</div>
														{(p.questionnaire?.budgetRange || p.questionnaire?.timeline) && (
															<p className="small mt-2 mb-0 text-muted d-flex flex-wrap align-items-center gap-2">
																{p.questionnaire.budgetRange && (
																	<span>
																		<i className="bi bi-currency-euro me-1"></i>
																		{p.questionnaire.budgetRange.min?.toLocaleString()}–{p.questionnaire.budgetRange.max?.toLocaleString()} EUR
																	</span>
																)}
																{p.questionnaire.budgetRange && p.questionnaire.timeline && <span>•</span>}
																{p.questionnaire.timeline && (
																	<span>
																		<i className="bi bi-clock me-1"></i>
																		{p.questionnaire.timeline}
																	</span>
																)}
															</p>
														)}
													</div>
											</div>
											<div className="card-footer bg-white border-top-0 pt-0 position-relative" style={{ zIndex: 2 }}>
												<div className="d-flex flex-wrap gap-2">
													<div className="btn btn-success flex-grow-1 d-flex align-items-center justify-content-center gap-2">
														<i className="bi bi-eye"></i>
														{copy('View plan', 'Plan ansehen', 'Pogledaj plan')}
													</div>
													{p.aiGenerated && (
														<button
															type="button"
															className="btn btn-outline-success"
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																navigate(`/app/plans/${p.id}`, {
																	state: { returnTo: `/app/seeker/plan/${p.id}` }
																});
															}}
														>
															<i className="bi bi-receipt me-1" />
															{copy('Build quote', 'Angebot', 'Ponuda')}
														</button>
													)}
													{(user?.role === 'company' || (user?.role === 'admin' && viewAsTarget?.role === 'company')) && (
														<button
															type="button"
															className="btn btn-outline-primary"
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																openPerPlanInvite(p.databaseId);
															}}
														>
															<i className="bi bi-person-plus me-1" />
															{copy('Invite', 'Einladen', 'Pozovi')}
														</button>
													)}
													{(user?.role === 'company' || (user?.role === 'admin' && viewAsTarget?.role === 'company')) &&
														(p.hasClientInvite || p.hasLinkedSeeker) &&
														p.databaseId &&
														perPlanInviteUserId && (
															<button
																type="button"
																className="btn btn-outline-secondary"
																title={copy('Client view settings', 'Kundenansicht', 'Postavke prikaza za klijenta')}
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	setVisibilityPlan(p);
																}}
															>
																<i className="bi bi-eye me-1" />
																{copy('Client view', 'Kundenansicht', 'Prikaz klijenta')}
															</button>
														)}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
						{sortBy === 'distance' && isGeocodingCities && (
							<div
								className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-start justify-content-center"
								style={{
									backgroundColor: 'rgba(255, 255, 255, 0.9)',
									zIndex: 10,
									borderRadius: '0.5rem',
									backdropFilter: 'blur(2px)',
									paddingTop: '2rem'
								}}
							>
								<div className="text-center p-4">
									<div className="spinner-border text-success mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
										<span className="visually-hidden">Loading...</span>
									</div>
									<p className="text-muted mb-0 fw-semibold">{copy('Calculating distances…', 'Entfernungen werden berechnet…', 'Računanje udaljenosti…')}</p>
								</div>
							</div>
						)}
					</div>
				</>
			)}

			{invitePlanId != null && (
				<div
					className="modal d-block"
					style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 20000, overflowY: 'auto' }}
					role="dialog"
					aria-modal="true"
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">
									{copy('Invite a seeker to this plan', 'Suchenden zu diesem Plan einladen', 'Pozovi tražitelja na ovaj plan')}
								</h5>
								<button
									type="button"
									className="btn-close"
									onClick={closePerPlanInvite}
									aria-label={copy('Close', 'Schließen', 'Zatvori')}
									disabled={inviteBusy}
								/>
							</div>
							<div className="modal-body">
								<p className="text-muted small">
									{copy(
										'Optional client email if they already have an account. You can always share the link.',
										'Optionale E-Mail, falls bereits ein Konto. Sie können den Link immer teilen.',
										'Neobavezno e-mail ako već ima račun. Poveznicu uvijek možete podijeliti.'
									)}
								</p>
								<input
									type="email"
									className="form-control mb-3"
									value={inviteEmail}
									onChange={(e) => setInviteEmail(e.target.value)}
									disabled={inviteBusy || !!inviteShareText}
									placeholder="name@example.com"
								/>
								<div className="form-check mb-3">
									<input
										id="dp-invite-edit"
										type="checkbox"
										className="form-check-input"
										checked={inviteAllowEdit}
										onChange={(e) => setInviteAllowEdit(e.target.checked)}
										disabled={inviteBusy || !!inviteShareText}
									/>
									<label className="form-check-label" htmlFor="dp-invite-edit">
										{copy('Allow client to edit', 'Bearbeitung erlauben', 'Dopusti uređivanje')}
									</label>
								</div>
								<ShareVisibilitySettings
									value={perPlanShareVisibility}
									onChange={setPerPlanShareVisibility}
									copy={copy}
									disabled={inviteBusy || !!inviteShareText}
									compact
								/>
								{inviteCode && inviteShareText && (
									<InviteSharePanel inviteCode={inviteCode} shareText={inviteShareText} copy={copy} />
								)}
							</div>
							<div className="modal-footer">
								<button
									type="button"
									className={`btn ${inviteShareText ? 'btn-success' : 'btn-outline-secondary'}`}
									onClick={closePerPlanInvite}
									disabled={inviteBusy}
								>
									{copy('Close', 'Schließen', 'Zatvori')}
								</button>
								{!inviteShareText && (
									<button type="button" className="btn btn-success" onClick={() => void submitPerPlanInvite()} disabled={inviteBusy}>
										{inviteBusy ? (
											<>
												<span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
												{copy('Creating…', 'Wird erstellt…', 'Stvaranje…')}
											</>
										) : (
											copy('Create invite link', 'Einladungslink erstellen', 'Stvori poveznicu')
										)}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{visibilityPlan?.databaseId && perPlanInviteUserId && (
				<ClientVisibilityModal
					open
					onClose={() => setVisibilityPlan(null)}
					planDatabaseId={visibilityPlan.databaseId}
					companyUserId={perPlanInviteUserId}
					copy={copy}
					initialVisibility={visibilityPlan.share_visibility}
					onSaved={(saved) => {
						setPlans((prev) =>
							prev.map((plan) =>
								plan.databaseId === visibilityPlan.databaseId ? { ...plan, share_visibility: saved } : plan
							)
						);
						showAlert(
							copy(
								'Client view updated. Your client will see the new settings immediately.',
								'Kundenansicht aktualisiert. Ihr Kunde sieht die neuen Einstellungen sofort.',
								'Prikaz za klijenta je ažuriran. Klijent će odmah vidjeti nove postavke.'
							),
							{ title: copy('Saved', 'Gespeichert', 'Spremljeno') }
						);
					}}
				/>
			)}
		</>
	);
}
